import { useCallback, ChangeEvent, useRef, useState, useEffect } from 'react';
import { useTus } from 'use-tus';
import { useGcal } from '@geerd-nx/gcal';
import { Button } from './../';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import {
  CheckIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ReactiveVar } from '@apollo/client';
import { Disclosure } from '@headlessui/react';

interface VideoUploaderArgs {
  ASSETS_API: string;
  videoUIvar?: ReactiveVar<string>;
  onCompleted?: (url: string) => void;
  defaultValue?: string;
}
export function VideoUploader({
  ASSETS_API,
  videoUIvar,
  onCompleted,
  defaultValue,
}: VideoUploaderArgs) {
  const { setUpload, upload, isSuccess, isAborted } = useTus();
  const { getSession } = useGcal();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadProgress, setProgress] = useState(0);
  const [url, setUrl] = useState('');
  const [fileName, setFileName] = useState('');
  useEffect(() => {
    if (isSuccess) {
      if (videoUIvar) {
        videoUIvar(`cloudflare://${upload?.url?.split('/')[4].split('?')[0]}`);
      }
      if (onCompleted) {
        onCompleted(`cloudflare://${upload?.url?.split('/')[4].split('?')[0]}`);
      }
    }
  }, [isSuccess, upload, videoUIvar]);

  const handleSetUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      // get signed post request
      const session = await getSession();
      const token = session.getAccessToken().getJwtToken();
      const file = event.target.files?.item(0);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        if (!file) {
          return;
        }

        setFileName(file?.name);
        setUpload(file, {
          endpoint: `${ASSETS_API}/api/video/tus-upload-url?token=${token}`,
          metadata: {
            filename: file.name,
            name: file.name,
            filetype: file.type,
            filesize: file.size.toString(),
            maxDurationSeconds: Math.round(video.duration).toString(),
            metadata: JSON.stringify({ origin: window.location.origin }),
          },
          chunkSize: 10 * 1024 * 1024, // Required a minimum chunk size of 15MB, here we use 50MB.
          retryDelays: [0, 3000, 5000, 10000, 20000],

          onError: function (error) {
            console.log('Failed because: ' + error);
          },

          // Callback for reporting upload progress
          onProgress: function (bytesUploaded, bytesTotal) {
            const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            setProgress(+percentage);
            console.log(bytesUploaded, bytesTotal, percentage + '%');
          },
          onSuccess: function () {
            // console.log('Download %s', file.name)
            console.log('upload file %s', file.name);
          },
        });
      };
      video.src = URL.createObjectURL(file as File);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUpload]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setUrl(event.target.value);
  };

  const handlelInsert = () => {
    if (videoUIvar) {
      videoUIvar(url);
    }
    if (onCompleted) {
      onCompleted(url);
    }
  };

  const handleStart = useCallback(() => {
    if (!upload) {
      return;
    }

    // Check if there are any previous uploads to continue.
    upload.findPreviousUploads().then(function (previousUploads) {
      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }

      // Start the upload
      upload.start();
    });
  }, [upload]);

  const handleAbort = useCallback(() => {
    if (!upload) {
      return;
    }

    upload.abort();
  }, [upload]);

  return (
    <div className="sm:mt-0 sm:col-span-3">
      <div className="flex max-w-lg relative rounded-md shadow-sm sm:max-w-xs">
        <div className="relative flex items-stretch flex-grow focus-within:z-10">
          <div className="border shadow-sm py-2 pl-3 focus:ring-primary-500 focus:border-primary-500 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300">
            {fileName ? fileName : 'Choose file...'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className=" relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          <span>Browse</span>
        </button>
        <input
          type="file"
          accept="video/*"
          onChange={handleSetUpload}
          hidden
          ref={fileInput}
        />
      </div>

      <div className=" bg-white py-2">
        <Disclosure defaultOpen={Boolean(defaultValue)}>
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between rounded-lg bg-primary-100 px-4 py-2 text-left text-sm font-medium text-primary-900 hover:bg-primary-200 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75">
                <label
                  htmlFor="company-website"
                  className="block text-sm font-medium leading-6 text-gray-900 cursor-pointer"
                >
                  Insert video via URL
                </label>
                <ChevronUpIcon
                  className={`${
                    open ? 'rotate-180 transform' : ''
                  } h-5 w-5 text-primary-500`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="pt-2 pb-2 text-sm text-gray-500">
                {({ close }) => (
                  <div className="border-b border-gray-200 bg-white px-2">
                    <div>
                      <div className="mt-2">
                        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600 sm:max-w-md">
                          <input
                            type="url"
                            defaultValue={defaultValue}
                            autoComplete="off"
                            name="company-website"
                            id="company-website"
                            className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                            placeholder=" http://www.example.com"
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className=" my-2 flex gap-2">
                      <button
                        onClick={handlelInsert}
                        type="button"
                        className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        <CheckIcon
                          className="-ml-0.5 h-5 w-5  text-green-500"
                          aria-hidden="true"
                        />
                        Insert
                      </button>
                      <button
                        onClick={() => {
                          if (videoUIvar) {
                            videoUIvar('');
                          }
                          if (onCompleted) {
                            onCompleted(``);
                          }
                          close();
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        <XMarkIcon
                          className="-ml-0.5 h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
      {/*  */}

      {upload && !isSuccess && (
        <div className="mt-4">
          <Button
            className="mr-3"
            type="button"
            color="primary"
            size="sm"
            onClick={handleStart}
            leftIcon={
              <PlayIcon aria-hidden="true" className="-ml-0.5 mr-2 h-4 w-4" />
            }
          >
            {isAborted ? 'Resume' : 'Upload'}
          </Button>

          {!!uploadProgress && (
            <Button
              type="button"
              color="secondary"
              size="sm"
              onClick={handleAbort}
              leftIcon={
                <PauseIcon
                  aria-hidden="true"
                  className="-ml-0.5 mr-2 h-4 w-4"
                />
              }
            >
              Pause
            </Button>
          )}
        </div>
      )}

      {!!uploadProgress && (
        <div className="w-full bg-gray-200 mt-2 rounded-full dark:bg-gray-700">
          <div
            className="bg-primary-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
            style={{ width: `${uploadProgress}%` }}
          >
            {uploadProgress}%
          </div>
        </div>
      )}
    </div>
  );
}
